import React, { createContext, useContext, useState, useEffect } from 'react';

type Theme = 'light' | 'dark';

export interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  colors: {
    background: string;
    surface: string;
    surfaceHover: string;
    primary: string;
    primaryHover: string;
    text: string;
    textSecondary: string;
    textMuted: string;
    border: string;
    borderLight: string;
    accent: string;
    danger: string;
    success: string;
    warning: string;
  };
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Linear.app inspired minimal color palette
const lightTheme = {
  background: '#ffffff',
  surface: '#fafafa',       // Clean neutral surface
  surfaceHover: '#f5f5f5',  // Subtle hover state
  primary: '#171717',
  primaryHover: '#262626',
  text: '#0a0a0a',          // Pure black for maximum contrast
  textSecondary: '#525252', // Neutral gray for secondary text
  textMuted: '#737373',     // Muted gray for less important text
  border: '#e5e5e5',        // Clean neutral border
  borderLight: '#f5f5f5',   // Very light border
  accent: '#0070f3',        // Linear's signature blue
  danger: '#ff4757',
  success: '#00a96e',
  warning: '#ff8c00',
};

const darkTheme = {
  background: '#0a0a0a',    // Pure neutral dark background (no blue tint)
  surface: '#141414',       // Neutral dark surface
  surfaceHover: '#1a1a1a',  // Subtle neutral hover state
  primary: '#f5f5f5',       // Clean white for primary elements
  primaryHover: '#e5e5e5',
  text: '#f5f5f5',          // High contrast white text
  textSecondary: '#a0a0a0', // Neutral gray for secondary text
  textMuted: '#707070',     // Muted but readable neutral gray
  border: '#2a2a2a',        // Very subtle neutral border
  borderLight: '#1a1a1a',   // Extremely subtle border
  accent: '#58a6ff',        // Keep blue accent for interactive elements
  danger: '#f85149',
  success: '#3fb950',
  warning: '#d29922',
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const saved = localStorage.getItem('apds-theme');
    return (saved as Theme) || 'light';
  });

  useEffect(() => {
    localStorage.setItem('apds-theme', theme);
    // Update CSS custom properties
    const colors = theme === 'light' ? lightTheme : darkTheme;
    Object.entries(colors).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--color-${key}`, value);
    });
    
    // Update document class for Tailwind dark mode
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const colors = theme === 'light' ? lightTheme : darkTheme;

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, colors }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
} 